import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "../../index.module.scss";
import style from "./style.module.scss";
import ChangeAvatar from "../ChangeAvatar/ChangeAvatar";
import EditIcon from "../../../shared/icons/EditIcon";
import InputMask from "react-input-mask";
import { Order, User } from "../../../../services";
import CustomMultiSelect from "../../../custom/custom-select/CustomMultiSelect";
import CustomDatePicker from "../../../shared/date-picker/Date";
import CalendarIcon from "../../../shared/icons/CalendarIcon";
import { toast } from "react-toastify";
import { useLanguage } from "../../../../context/LanguageContext";
import CustomButton from "../../../custom/custom-button/CustomButton";
import ChangeEmailModal from "../ChangeEmailModal/ChangeEmailModal";

const ProfileEdit = ({ user, onCancel, onSave, companyData = null }) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const BIN_LENGTH = 12;

  const [tempUser, setTempUser] = useState({
    name: user.name || "",
    surname: user?.surname || "",
    patronymic: user?.patronymic || "",
    phoneNumber: user?.phoneNumber || "",
    address: user?.address || "",
    company: {
      name: companyData?.name || user?.companyName || "",
      bin: companyData?.bin || user?.bin || "",
      isNDS: companyData?.isNDS !== undefined ? companyData.isNDS : (user?.isNDS !== undefined ? user?.isNDS : false),
      foundedYear: companyData?.foundedYear || user.company?.foundedYear || "",
      cargoType: companyData?.cargoType || user.company?.cargoType || [],
      avgVolume: companyData?.avgVolume || user.company?.avgVolume || "",
      regions: companyData?.regions || user.company?.regions || [],
      contact: companyData?.contact || user.company?.contact || "",
      description: companyData?.description || user.company?.description || "",
    },
  });

  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCargoTypes, setSelectedCargoTypes] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);

  const [isChangeEmailOpen, setIsChangeEmailOpen] = useState(false);

  const [errors, setErrors] = useState({
    name: "",
    surname: "",
    phoneNumber: "",
    companyBin: "",
    companyName: "",
  });

  const parseFoundedYear = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split(".");
    if (parts.length === 3) {
      return new Date(parts[2], parts[1] - 1, parts[0]);
    }
    return null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesResponse, countriesResponse] = await Promise.all([
          Order.getCategories(currentLanguage),
          Order.getCountries(currentLanguage),
        ]);
        setCategories(categoriesResponse.data || []);
        setCountries(countriesResponse.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [currentLanguage]);

  useEffect(() => {
    if (companyData?.cargoType) {
      try {
        const parsedCargoTypes = JSON.parse(companyData.cargoType);
        setSelectedCargoTypes(Array.isArray(parsedCargoTypes) ? parsedCargoTypes : []);
      } catch (error) {
        console.error("Error parsing cargoType:", error);
        setSelectedCargoTypes([]);
      }
    } else {
      setSelectedCargoTypes([]);
    }

    if (companyData?.regions) {
      try {
        const parsedRegions = JSON.parse(companyData.regions);
        setSelectedRegions(Array.isArray(parsedRegions) ? parsedRegions : []);
      } catch (error) {
        console.error("Error parsing regions:", error);
        setSelectedRegions([]);
      }
    } else {
      setSelectedRegions([]);
    }
  }, [companyData]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name.startsWith("company.")) {
      const fieldName = name.split(".")[1];
      if (type === "checkbox") {
        setTempUser((prevState) => ({
          ...prevState,
          company: { ...prevState.company, [fieldName]: checked },
        }));
      } else {
        setTempUser((prevState) => ({
          ...prevState,
          company: { ...prevState.company, [fieldName]: value },
        }));
      }
    } else {
      if (type === "checkbox") {
        setTempUser((prevState) => ({ ...prevState, [name]: checked }));
      } else {
        setTempUser((prevState) => ({ ...prevState, [name]: value }));
      }
    }
  };

  const handleCargoTypeChange = (selectedItems) => {
    setSelectedCargoTypes(selectedItems);
    const filteredItems = selectedItems.map((item) => ({
      id: item.id,
      name: item.name,
    }));
    setTempUser((prevState) => ({
      ...prevState,
      company: {
        ...prevState.company,
        cargoType: JSON.stringify(filteredItems),
      },
    }));
  };

  const handleRegionsChange = (selectedItems) => {
    setSelectedRegions(selectedItems);
    const filteredItems = selectedItems.map((item) => ({
      id: item.id,
      name: item.name,
    }));
    setTempUser((prevState) => ({
      ...prevState,
      company: {
        ...prevState.company,
        regions: JSON.stringify(filteredItems),
      },
    }));
  };

  const handleFoundedYearChange = (date) => {
    const formattedDate = date
      ? `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(
          2,
          "0",
        )}.${date.getFullYear()}`
      : "";

    setTempUser((prevState) => ({
      ...prevState,
      company: {
        ...prevState.company,
        foundedYear: formattedDate,
      },
    }));
  };

  const removeEmptyFields = (obj) => {
    const cleaned = {};

    Object.keys(obj).forEach((key) => {
      const value = obj[key];

      if (value && typeof value === "object" && !Array.isArray(value)) {
        const cleanedNested = removeEmptyFields(value);
        if (Object.keys(cleanedNested).length > 0) {
          cleaned[key] = cleanedNested;
        }
      } else if (typeof value === "boolean") {
        cleaned[key] = value;
      } else if (value !== "" && value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            cleaned[key] = value;
          }
        } else if (typeof value === "string") {
          const trimmed = value.trim();
          if (trimmed !== "" && trimmed !== "[]" && trimmed !== "{}" && trimmed.toLowerCase() !== "null") {
            cleaned[key] = value;
          }
        } else {
          cleaned[key] = value;
        }
      }
    });

    return cleaned;
  };

  const validateRequiredFields = () => {
    const newErrors = {
      name: "",
      surname: "",
      phoneNumber: "",
      companyBin: "",
      companyName: "",
    };

    let isValid = true;

    if (!tempUser.name?.trim()) {
      newErrors.name = t("profileTwo.validation.invalidName") || "Введите имя";
      isValid = false;
    }

    if (!tempUser.surname?.trim()) {
      newErrors.surname = t("profileTwo.validation.invalidLastName") || "Введите фамилию";
      isValid = false;
    }

    const cleanPhone = tempUser.phoneNumber?.replace(/\D/g, "") || "";
    if (!tempUser.phoneNumber || cleanPhone.length < 11) {
      newErrors.phoneNumber = t("profileTwo.validation.invalidPhone") || "Введите корректный номер телефона";
      isValid = false;
    }

    if (!tempUser.company?.bin?.trim() || tempUser.company.bin.length !== BIN_LENGTH) {
      newErrors.companyBin = t("profileTwo.validation.invalidIin") || "Введите корректный БИН (12 цифр)";
      isValid = false;
    }

    if (!tempUser.company?.name?.trim()) {
      newErrors.companyName = t("profileTwo.validation.invalidCompanyName") || "Введите название компании";
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      Object.values(newErrors).forEach((error) => {
        if (error) {
          toast.error(error);
        }
      });
    }

    return isValid;
  };

  const handleSaveChanges = async () => {
    if (!validateRequiredFields()) {
      return;
    }

    const cleanedData = removeEmptyFields(tempUser);

    try {
      const response = await User.update(cleanedData);
      if (response.status === 200) {
        console.log(t("profileTwo.toast.saveSuccess"));
        toast.success(t("profileTwo.toast.saveSuccess"));
        onSave();
      } else {
        console.log(t("profileTwo.toast.saveError"));
      }
    } catch (error) {
      console.error(t("profileTwo.toast.saveError"), error);
      toast.error(t("profileTwo.toast.saveError"));
    }
  };

  const handleCancelClick = () => {
    onCancel();
  };

  const isExpeditor = user?.type === "Expeditor";

  return (
    <div>
      <div className={styles["profile__company-header"]}>
        <ChangeAvatar onOpenModal={() => {}} user={user} />
        <div className="profile__company-df">
          <div className={styles["profile__contact-title"]}>{t("profileTwo.editForm.lastNameLabel")}</div>
          <input
            className={`${styles["profile__contact-edit"]} ${errors.surname ? styles["profile__contact-edit-error"] : ""}`}
            name="surname"
            value={tempUser.surname}
            onChange={(e) => {
              handleInputChange(e);
              if (errors.surname) setErrors((prev) => ({ ...prev, surname: "" }));
            }}
            size="lg"
            type="text"
            placeholder={t("profileTwo.editForm.lastNamePlaceholder")}
          />
          <EditIcon />

          <div className={styles["profile__contact-title"]}>{t("profileTwo.editForm.firstNameLabel")}</div>
          <input
            className={`${styles["profile__contact-edit"]} ${errors.name ? styles["profile__contact-edit-error"] : ""}`}
            name="name"
            value={tempUser.name}
            onChange={(e) => {
              handleInputChange(e);
              if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
            }}
            size="lg"
            type="text"
            placeholder={t("profileTwo.editForm.firstNamePlaceholder")}
          />
          <EditIcon />

          <div className={styles["profile__contact-title"]}>{t("profileTwo.editForm.patronymicLabel")}</div>
          <input
            className={styles["profile__contact-edit"]}
            name="patronymic"
            value={tempUser.patronymic}
            onChange={handleInputChange}
            size="lg"
            type="text"
            placeholder={t("profileTwo.editForm.patronymicLabel")}
          />
          <EditIcon />
        </div>
      </div>

      <div className={styles["profile__contact"]}>
        <div className={styles["profile__contact-title"]}>{t("profileTwo.contactInfo.binLabel")}</div>
        <input
          className={`${styles["profile__contact-edit"]} ${errors.companyBin ? styles["profile__contact-edit-error"] : ""}`}
          name="company.bin"
          value={tempUser.company?.bin}
          onChange={(e) => {
            handleInputChange(e);
            if (errors.companyBin) setErrors((prev) => ({ ...prev, companyBin: "" }));
          }}
          size="lg"
          type="text"
          placeholder="123456789012"
          maxLength={BIN_LENGTH}
        />
        <EditIcon />

        <div className={styles["profile__contact-title"]}>{t("profileTwo.contactInfo.phoneLabel")}</div>
        <InputMask
          className={`${styles["profile__contact-edit"]} ${errors.phoneNumber ? styles["profile__contact-edit-error"] : ""}`}
          name="phoneNumber"
          value={tempUser.phoneNumber}
          onChange={(e) => {
            handleInputChange(e);
            if (errors.phoneNumber) setErrors((prev) => ({ ...prev, phoneNumber: "" }));
          }}
          mask="+7 (999) 999-99-99"
          maskChar="_"
          size="lg"
          type="text"
          placeholder={t("profileTwo.editForm.phonePlaceholder")}
        />
        <EditIcon />

        <div className={styles["profile__contact-title"]}>{t("profileTwo.contactInfo.emailLabel")}</div>
        <div style={{display: "flex"}}>
          <div className={styles["profile__contact-subtitle"]}>{user.email}</div> 
          <CustomButton text={t('confirmEmailComponent.buttons.changeEmail')} isUnderline onClick={() => setIsChangeEmailOpen(true)} />
        </div>

        <div className={styles["profile__contact-title"]}>{t("profileTwo.company.nameLabel")}</div>
        <input
          className={`${styles["profile__contact-edit"]} ${errors.companyName ? styles["profile__contact-edit-error"] : ""}`}
          name="company.name"
          value={tempUser.company.name}
          onChange={(e) => {
            handleInputChange(e);
            if (errors.companyName) setErrors((prev) => ({ ...prev, companyName: "" }));
          }}
          size="lg"
          type="text"
          placeholder={t("profileTwo.company.namePlaceholder")}
        />
        <EditIcon />

        <div className={styles["profile__contact-title"]}>{t("profileTwo.company.foundedYearLabel")}</div>
        <div>
          <CustomDatePicker
            selected={parseFoundedYear(tempUser.company.foundedYear)}
            onChange={handleFoundedYearChange}
            placeholderText={t("profileTwo.company.foundedYearPlaceholder")}
            withBorder
            IconStart={<CalendarIcon />}
            showYearNavigation
          />
        </div>

        {!isExpeditor && (
          <>
            <div className={styles["profile__contact-title"]}>{t("profileTwo.company.avgVolumeLabel")}</div>
            <input
              className={styles["profile__contact-edit"]}
              name="company.avgVolume"
              value={tempUser.company.avgVolume}
              onChange={handleInputChange}
              size="lg"
              type="number"
              placeholder={t("profileTwo.company.avgVolumePlaceholder")}
            />
            <EditIcon />
          </>
        )}

        {!isExpeditor && (
          <div style={{ marginTop: "20px" }}>
            <CustomMultiSelect
              labelText={t("profileTwo.company.cargoTypeLabel")}
              placeholder={t("profileTwo.company.cargoTypePlaceholder")}
              options={categories}
              keyId="id"
              keyName="name"
              defaultValue={selectedCargoTypes}
              onSubmit={handleCargoTypeChange}
            />
          </div>
        )}

        <div style={{ marginTop: "20px" }}>
          <CustomMultiSelect
            labelText={t("profileTwo.company.regionsLabel")}
            placeholder={t("profileTwo.company.regionsPlaceholder")}
            options={countries}
            keyId="id"
            keyName="name"
            defaultValue={selectedRegions}
            onSubmit={handleRegionsChange}
          />
        </div>

        <div className="form-check" style={{ marginTop: "20px" }}>
          <label className="form-check-label" htmlFor="flexCheckChecked">
            {t("profileTwo.editForm.ndsLabel")}
          </label>
          <input
            className="form-check-input"
            type="checkbox"
            id="flexCheckChecked"
            name="company.isNDS"
            checked={tempUser.company.isNDS}
            onChange={handleInputChange}
          />
        </div>

        <div className={styles["profile__contact-title"]}>{t("profileTwo.contactInfo.addressLabel")}</div>
        <textarea
          className="form-control textarea_edit"
          style={{ border: "1.6px solid #ECEDEE", marginTop: "12px" }}
          id="addressTextarea"
          rows="2"
          name="address"
          value={tempUser.address}
          onChange={handleInputChange}
          size="lg"
          type="text"
          placeholder={t("profileTwo.editForm.addressPlaceholder")}
        ></textarea>

        <div className={styles["profile__contact-title"]}>{t("profileTwo.company.contactLabel")}</div>
        <textarea
          className="form-control textarea_edit"
          style={{ border: "1.6px solid #ECEDEE", marginTop: "12px" }}
          id="contactTextarea"
          rows="3"
          name="company.contact"
          value={tempUser.company.contact}
          onChange={handleInputChange}
          placeholder={t("profileTwo.company.contactPlaceholder")}
        ></textarea>

        <div className={styles["profile__contact-title"]}>{t("profileTwo.company.descriptionLabel")}</div>
        <textarea
          className="form-control textarea_edit"
          style={{ border: "1.6px solid #ECEDEE", marginTop: "12px" }}
          id="descriptionTextarea"
          rows="3"
          name="company.description"
          value={tempUser.company.description}
          onChange={handleInputChange}
          placeholder={t("profileTwo.company.descriptionPlaceholder")}
        ></textarea>

        <div className={styles["profile__upload-block"]}>
          <button className={styles["profile__upload-button"]} onClick={handleSaveChanges}>
            {t("profileTwo.buttons.saveChanges")}
          </button>
          <button className={styles["profile__upload-button"]} onClick={handleCancelClick}>
            {t("profileTwo.buttons.cancelChanges")}
          </button>
        </div>
      </div>

      <ChangeEmailModal
        isOpen={isChangeEmailOpen}
        onClose={() => setIsChangeEmailOpen(false)}
        currentEmail={user.email}
      />
    </div>
  );
};

export default ProfileEdit;
